import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export type AlertHorizon = "intraday" | "short" | "medium" | "long";
export type AlertSide = "bullish" | "bearish";
export type PositionStatus = "open" | "closed" | "timeout";
export type PositionOrder = "recent" | "best" | "worst";

export interface AlertRule {
  horizon: AlertHorizon;
  side: AlertSide;
  enabled: boolean;
  entry_floor: number;
  entry_ceiling: number;
  exit_threshold: number;
}

export interface Position {
  id: number;
  ticker: string;
  yahoo_symbol: string;
  horizon: AlertHorizon;
  side: AlertSide;
  status: PositionStatus;
  open_score: number;
  open_price: number | null;
  opened_at: string;
  close_score: number | null;
  close_price: number | null;
  closed_at: string | null;
  close_reason: string | null;
  raw_return: number | null;
  spy_return: number | null;
  excess_return: number | null;
  hit: boolean | null;
}

export interface PositionStats {
  n_closed: number;
  n_open: number;
  win_rate: number | null;
  avg_raw: number | null;
  avg_excess: number | null;
  median_holding_days: number | null;
}

export const HORIZON_ORDER: AlertHorizon[] = [
  "intraday",
  "short",
  "medium",
  "long",
];

export const HORIZON_LABEL: Record<AlertHorizon, string> = {
  intraday: "Intraday",
  short: "Short (~1 week)",
  medium: "Medium (~1 month)",
  long: "Long (~6 months)",
};

export const SIDE_LABEL: Record<AlertSide, string> = {
  bullish: "Buy alerts",
  bearish: "Sell-short alerts",
};

export function useAlertRules() {
  return useQuery<AlertRule[]>({
    queryKey: ["alert-rules"],
    queryFn: async () => (await api.get<AlertRule[]>("/me/alert-rules")).data,
    staleTime: 60_000,
  });
}

export function useUpdateAlertRule() {
  const qc = useQueryClient();
  return useMutation<
    AlertRule,
    Error,
    {
      horizon: AlertHorizon;
      side: AlertSide;
      patch: Partial<Pick<
        AlertRule,
        "enabled" | "entry_floor" | "entry_ceiling" | "exit_threshold"
      >>;
    }
  >({
    mutationFn: async ({ horizon, side, patch }) =>
      (
        await api.put<AlertRule>(`/me/alert-rules/${horizon}/${side}`, patch)
      ).data,
    onSuccess: (data, vars) => {
      qc.setQueryData<AlertRule[]>(["alert-rules"], (prev) =>
        (prev ?? []).map((r) =>
          r.horizon === vars.horizon && r.side === vars.side ? data : r,
        ),
      );
    },
  });
}

export function useHindsightPositions(opts: {
  horizon?: AlertHorizon | "all";
  side?: AlertSide | "both";
  order?: PositionOrder;
  limit?: number;
}) {
  const params = new URLSearchParams();
  if (opts.horizon && opts.horizon !== "all") params.set("horizon", opts.horizon);
  if (opts.side && opts.side !== "both") params.set("side", opts.side);
  if (opts.order) params.set("order", opts.order);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return useQuery<Position[]>({
    queryKey: ["hindsight-positions", opts],
    queryFn: async () =>
      (
        await api.get<Position[]>(
          `/hindsight/positions${qs ? `?${qs}` : ""}`,
        )
      ).data,
    staleTime: 30_000,
  });
}

export function useHindsightPositionStats(opts: {
  horizon?: AlertHorizon | "all";
  side?: AlertSide | "both";
}) {
  const params = new URLSearchParams();
  if (opts.horizon && opts.horizon !== "all") params.set("horizon", opts.horizon);
  if (opts.side && opts.side !== "both") params.set("side", opts.side);
  const qs = params.toString();
  return useQuery<PositionStats>({
    queryKey: ["hindsight-positions-stats", opts],
    queryFn: async () =>
      (
        await api.get<PositionStats>(
          `/hindsight/positions/stats${qs ? `?${qs}` : ""}`,
        )
      ).data,
    staleTime: 30_000,
  });
}

export function useMyPositions(opts: {
  status?: PositionStatus;
  horizon?: AlertHorizon;
  side?: AlertSide;
  limit?: number;
}) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.horizon) params.set("horizon", opts.horizon);
  if (opts.side) params.set("side", opts.side);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return useQuery<Position[]>({
    queryKey: ["my-positions", opts],
    queryFn: async () =>
      (
        await api.get<Position[]>(
          `/me/positions${qs ? `?${qs}` : ""}`,
        )
      ).data,
    staleTime: 30_000,
  });
}
